/**
 * Data Bundle View Modal — Read-only right-side drawer
 */

import React, { useState, useEffect } from 'react';
import { activeLocale } from '@/utils/locale';
import { FilePreviewModal } from '@/components/common';
import { useLanguage } from '@/hooks/useLanguage';
import { ContextPromptViewer } from '@/components/common/ContextPromptViewer';
import { DataBundleApi } from '../services/dataBundleService';
import type { DataBundle, DataBundleFile, SyncFolder } from '../types';
import './DataBundleViewModal.css';
import type { TFunc } from '@/utils/tfunc';

interface DataBundleViewModalProps {
  visible: boolean;
  bundle: DataBundle | null;
  onClose: () => void;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateTime(d: string | null | undefined, never: string): string {
  if (!d) return never;
  return new Date(d).toLocaleString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function FileIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
}

function SectionIcon({ icon }: { icon: 'files' | 'folders' | 'info' }) {
  if (icon === 'files') return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
  if (icon === 'folders') return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

function getSourceLabel(t: TFunc, sourceType: DataBundleFile['source_type']): string {
  if (sourceType === 'upload') return t('dataBundles.sourceUploaded');
  if (sourceType === 'cloud_file') return t('cloudStorage.cloudFile');
  if (sourceType === 'cloud_sync') return t('dataBundles.sourceSynced');
  return sourceType;
}

function isCloudSource(sourceType: DataBundleFile['source_type']): boolean {
  return sourceType === 'cloud_file' || sourceType === 'cloud_sync';
}

export const DataBundleViewModal: React.FC<DataBundleViewModalProps> = ({ visible, bundle, onClose }) => {
  const { t } = useLanguage();
  const [fullBundle, setFullBundle] = useState<DataBundle | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ filePath?: string; fileId?: string; fileName: string; fileType: string } | null>(null);

  useEffect(() => {
    if (!visible || !bundle) { setFullBundle(null); return; }
    setLoading(true);
    DataBundleApi.getBundle(bundle.data_bundle_id)
      .then(fetched => setFullBundle(fetched))
      .catch(err => console.error('Error loading bundle:', err))
      .finally(() => setLoading(false));
  }, [visible, bundle]);

  if (!visible) return null;

  const data = fullBundle || bundle;
  const files: DataBundleFile[] = fullBundle?.files || [];
  const syncFolders: SyncFolder[] = data?.sync_folders || [];

  return (
    <div className="bundle-view-overlay" onClick={onClose}>
      <div className="bundle-view-drawer" onClick={e => e.stopPropagation()}>
        {/* Accent strip */}
        <div className="bvm-accent-bar" />

        {/* Header */}
        <div className="bvm-header">
          <div className="bvm-header-icon">📦</div>
          <div className="bvm-header-body">
            <div className="bvm-header-name">{data?.bundle_name || '...'}</div>
            <div className="bvm-header-chips">
              {data?.is_shared && <span className="bvm-chip bvm-chip-shared">{t('common.shared')}</span>}
              {data && (
                <span className="bvm-chip bvm-chip-files">
                  {t('counts.files', { count: data.file_count ?? 0 })}
                </span>
              )}
            </div>
          </div>
          <button type="button" className="bvm-header-close" onClick={onClose} aria-label={t('common.close')}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {loading ? (
          <div className="bvm-loading">
            <div className="bvm-spinner" />
            {t('dataBundles.loadingBundleDetails')}
          </div>
        ) : data && (
          <>
            {/* Stats row */}
            <div className="bvm-stats">
              <div className="bvm-stat">
                <span className="bvm-stat-value">{files.length || data.file_count || 0}</span>
                <span className="bvm-stat-label">{t('dataBundles.files')}</span>
              </div>
              <div className="bvm-stat">
                <span className="bvm-stat-value">{syncFolders.length}</span>
                <span className="bvm-stat-label">{t('dataBundles.syncFolders')}</span>
              </div>
              <div className="bvm-stat">
                <span className="bvm-stat-value">{(data as any).bundle_context_prompt ? 'Yes' : 'No'}</span>
                <span className="bvm-stat-label">{t('dataBundles.contextPrompt')}</span>
              </div>
            </div>

            {/* Meta */}
            <div className="bvm-meta">
              <div className="bvm-meta-item">
                <span className="bvm-meta-label">{t('dataBundles.createdBy')}</span>
                <span className="bvm-meta-value">{data.created_by_name || '—'}</span>
              </div>
              <div className="bvm-meta-item">
                <span className="bvm-meta-label">{t('dataBundles.created')}</span>
                <span className="bvm-meta-value">{formatDate(data.created_at)}</span>
              </div>
              <div className="bvm-meta-item">
                <span className="bvm-meta-label">{t('dataBundles.lastUpdated')}</span>
                <span className="bvm-meta-value">{formatDate(data.updated_at)}</span>
              </div>
            </div>

            <div className="bvm-body">
              {/* Description */}
              {data.bundle_description && (
                <div className="bvm-section">
                  <div className="bvm-section-header">
                    <span className="bvm-section-icon"><SectionIcon icon="info" /></span>
                    <span className="bvm-section-title">{t('skills.description')}</span>
                  </div>
                  <p className="bvm-description" dir="auto">{data.bundle_description}</p>
                </div>
              )}

              {/* Bundle Context Prompt — auto-syncs on every file
                  change (see auto-file-sync feature). Collapsed
                  read-only view; admins can expand to inspect, no
                  edit affordance. */}
              <div className="bvm-section">
                <ContextPromptViewer
                  prompt={(data as any).bundle_context_prompt}
                  label={t('dataBundles.bundleContextPrompt')}
                  defaultOpen
                />
              </div>

              {/* Files */}
              <div className="bvm-section">
                <div className="bvm-section-header">
                  <span className="bvm-section-icon"><SectionIcon icon="files" /></span>
                  <span className="bvm-section-title">{t('dataBundles.files')}</span>
                  {files.length > 0 && <span className="bvm-section-count">{files.length}</span>}
                </div>
                {files.length === 0 ? (
                  <div className="bvm-empty">{t('dataBundles.noFilesInBundle')}</div>
                ) : files.map(f => (
                  <div
                    key={f.bundle_file_id}
                    className={`bvm-file-item ${f.file_path ? 'bvm-file-clickable' : ''}`}
                    onClick={() => {
                      if (f.file_id || f.file_path) {
                        setPreviewFile({
                          fileId: f.file_id,
                          filePath: f.file_path,
                          fileName: f.file_display_name || f.file_name || 'Unknown',
                          fileType: f.file_type || '',
                        });
                      }
                    }}
                    role={f.file_path ? 'button' : undefined}
                    tabIndex={f.file_path ? 0 : undefined}
                  >
                    <div className="bvm-file-icon-wrap"><FileIcon /></div>
                    <div className="bvm-file-info">
                      <div className="bvm-file-name" dir="auto">{f.file_display_name}</div>
                      <div className="bvm-file-meta">
                        <span className={`bvm-source-chip ${isCloudSource(f.source_type) ? 'bvm-source-cloud' : 'bvm-source-upload'}`} dir="auto">
                          {getSourceLabel(t, f.source_type)}
                        </span>
                        {f.cloud_provider && (
                          <span style={{ fontSize: '11px', color: 'var(--claude-text-muted)' }}>{f.cloud_provider}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sync Folders */}
              <div className="bvm-section">
                <div className="bvm-section-header">
                  <span className="bvm-section-icon"><SectionIcon icon="folders" /></span>
                  <span className="bvm-section-title">{t('dataBundles.syncFolders')}</span>
                  {syncFolders.length > 0 && <span className="bvm-section-count">{syncFolders.length}</span>}
                </div>
                {syncFolders.length === 0 ? (
                  <div className="bvm-empty">{t('dataBundles.noSyncFolders')}</div>
                ) : syncFolders.map(folder => (
                  <div key={folder.folder_id} className="bvm-sync-item">
                    <div className="bvm-sync-icon-wrap"><FolderIcon /></div>
                    <div className="bvm-sync-info">
                      <div className="bvm-sync-name" dir="auto">{folder.folder_name}</div>
                      <div className="bvm-sync-meta">
                        <span className="bvm-sync-provider">{folder.provider}</span>
                        {folder.folder_path && (
                          <span dir="auto" className="bvm-sync-meta-row">{folder.folder_path}</span>
                        )}
                        <span dir="auto" className="bvm-sync-meta-row">
                          {t('dataBundles.lastSynced', { when: formatDateTime(folder.last_sync_at, t('common.never')) })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="bvm-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>

      <FilePreviewModal
        visible={!!previewFile}
        filePath={previewFile?.filePath}
        fileId={previewFile?.fileId}
        fileName={previewFile?.fileName || ''}
        fileType={previewFile?.fileType}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
};
